import React from "react";
import { AssessmentDecision } from "reports/AssessmentGenerator";

interface AssessmentDeterminationsProps {
    decisions: AssessmentDecision[];
    showTooltips?: boolean;
}

const AssessmentDeterminations: React.FC<AssessmentDeterminationsProps> = ({ decisions, showTooltips = true }) => {
    const getCategoryBadgeClass = (category: string) => {
        switch (category) {
            case "physical":
                return "bg-blue-100 text-blue-800";
            case "behavioral":
                return "bg-purple-100 text-purple-800";
            case "continuedNeeds":
                return "bg-orange-100 text-orange-800";
            case "excluded":
                return "bg-gray-100 text-gray-600";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getCategoryLabel = (category: string) => {
        switch (category) {
            case "physical":
                return "Physical";
            case "behavioral":
                return "Behavioral";
            case "continuedNeeds":
                return "Continued Needs";
            case "excluded":
                return "Excluded";
            default:
                return category;
        }
    };

    const renderDecisionRow = (decision: AssessmentDecision, index: number) => {
        return (
            <tr key={index} className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 text-sm font-medium text-gray-900">
                    {decision.trajectory.measureName}
                </td>
                <td className="px-3 py-2 text-sm text-center">
                    <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getCategoryBadgeClass(decision.category)}`}
                        title={showTooltips ? decision.reason : undefined}
                    >
                        {getCategoryLabel(decision.category)}
                    </span>
                </td>
                <td className="px-3 py-2 text-sm text-gray-700">
                    {decision.reason}
                </td>
                <td className="px-3 py-2 text-sm text-center">
                    <span className={`font-medium ${decision.details.isFunctional ? 'text-green-600' : 'text-red-600'}`}>
                        {decision.details.isFunctional ? 'Yes' : 'No'}
                    </span>
                </td>
            </tr>
        );
    };

    const renderCategorySection = (title: string, categoryDecisions: AssessmentDecision[], categoryColor: string) => {
        if (categoryDecisions.length === 0) return null;

        return (
            <div className="mb-4">
                <h4 className={`text-sm font-semibold mb-2 px-3 py-1 rounded inline-block ${categoryColor}`}>
                    {title} ({categoryDecisions.length})
                </h4>
                <table className="w-full border rounded-lg overflow-hidden">
                    <colgroup>
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "12%", minWidth: "120px" }} />
                        <col style={{ width: "56%" }} />
                        <col style={{ width: "12%" }} />
                    </colgroup>
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Measure</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Category</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Decision Reason</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Functional</th>
                        </tr>
                    </thead>
                    <tbody>
                        {categoryDecisions.map((decision, idx) => renderDecisionRow(decision, idx))}
                    </tbody>
                </table>
            </div>
        );
    };

    // Helper function to determine if a measure is a ROM (Range of Motion) measure
    const isRomMeasure = (measureName: string): boolean => {
        const romKeywords = ['abduction', 'flexion', 'scaption', 'extension', 'rotation'];
        const lowerName = measureName.toLowerCase();
        return romKeywords.some(keyword => lowerName.includes(keyword));
    };

    // Sort function: ROM measures first (alphabetically), then others (alphabetically)
    const sortDecisions = (decisionsArray: AssessmentDecision[]): AssessmentDecision[] => {
        const romDecisions = decisionsArray.filter(d => isRomMeasure(d.trajectory.measureName));
        const otherDecisions = decisionsArray.filter(d => !isRomMeasure(d.trajectory.measureName));

        romDecisions.sort((a, b) => a.trajectory.measureName.localeCompare(b.trajectory.measureName));
        otherDecisions.sort((a, b) => a.trajectory.measureName.localeCompare(b.trajectory.measureName));

        return [...romDecisions, ...otherDecisions];
    };

    const physicalDecisions = sortDecisions(decisions.filter(d => d.category === "physical"));
    const behavioralDecisions = sortDecisions(decisions.filter(d => d.category === "behavioral"));
    const continuedNeedsDecisions = sortDecisions(decisions.filter(d => d.category === "continuedNeeds"));
    const excludedDecisions = sortDecisions(decisions.filter(d => d.category === "excluded"));

    return (
        <div>
            <div className="mb-4 bg-yellow-50 border border-orange-500 rounded text-sm">
                <div className="font-semibold text-gray-900 bg-orange-100 px-3 py-2 rounded-t">Categorization Rules</div>
                <div className="space-y-1 text-gray-700 p-3">
                    <div className="flex"><strong className="inline-block text-right" style={{ width: '185px' }}>Physical/Behavioral/Social:</strong> <span className="ml-2">Improved into higher score band</span></div>
                    <div className="flex"><strong className="inline-block text-right" style={{ width: '185px' }}>Continued Needs:</strong> <span className="ml-2">Did NOT improve into higher score band and NOT functional</span></div>
                    <div className="flex"><strong className="inline-block text-right" style={{ width: '185px' }}>Excluded:</strong> <span className="ml-2">Remained in same score band AND function or NOT included in report</span></div>
                </div>
            </div>
            {renderCategorySection("Physical Assessments", physicalDecisions, "bg-blue-100 text-blue-800")}
            {renderCategorySection("Behavioral Assessments", behavioralDecisions, "bg-purple-100 text-purple-800")}
            {renderCategorySection("Continued Needs Assessments", continuedNeedsDecisions, "bg-orange-100 text-orange-800")}
            {renderCategorySection("Excluded Assessments", excludedDecisions, "bg-gray-100 text-gray-700")}
        </div>
    );
};

export default AssessmentDeterminations;
